import {
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { MouseEventHandler, useId } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import Button from '../Button/Button';
import SaveCancelButtons from '../SaveCancelComponent/SaveCancelButtons';
import { MODAL_OPENED } from '../../../../modules/analytics/events';
import { withTracking } from '../../../../modules/analytics/withTracking';

// Custom style-only props consumed by the styled() args below; none should reach the DOM
// (forwarding them makes React warn "does not recognize the `minWidth` prop", etc.).
const STYLE_ONLY_PROPS = new Set([
  'width',
  'maxWidth',
  'minWidth',
  'minHeight',
  'height',
  'maxHeight',
  'dialogStyles',
  'padding',
  'contentPadding',
  'hideScrollbar',
]);

const DialogBoxStyle = styled(MuiDialog, {
  shouldForwardProp: prop => !STYLE_ONLY_PROPS.has(prop as string),
})<{
  width?: string | number;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string | number;
  minHeight?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string | number;
  height?: string | number;
  dialogStyles?: any;
  minWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string | number;
  maxHeight?: string | number;
  padding?: string | number;
  contentPadding?: string | number;
  hideScrollbar?: boolean;
}>(
  ({
    theme,
    width,
    maxWidth,
    height,
    minHeight,
    minWidth,
    dialogStyles,
    padding,
    contentPadding,
    maxHeight,
    hideScrollbar,
  }) => ({
    '& .MuiDialog-paper': {
      padding: padding || theme.spacing(4),
      margin: 'auto',
      minWidth: minWidth || '200px',
      minHeight: minHeight || '200px',
      width: width || 'auto',
      maxWidth: maxWidth || '1000px',
      borderRadius: theme.shape.borderRadius * 4,
      height: height || 'auto',
      maxHeight: maxHeight || 'auto',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        margin: '16px',
        maxWidth: '100%',
        minWidth: 'auto',
      },
      ...dialogStyles,
    },
    '& .MuiDialogContent-root': {
      padding: contentPadding || '10px 0 0 0',
      ...(hideScrollbar && {
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome/Safari
        },
      }),
      height: '90%'
    },
    '& .MuiDialogActions-root': {
      // paddingLeft: theme.spacing(4),
      // paddingRight: theme.spacing(4),
    },
  })
);

const DialogTitleContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  minWidth: 0, // let the title cell shrink so long titles wrap instead of overflowing
  // padding: theme.spacing(0), // Adjusted padding
}));

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  margin: 0,
  // padding: theme.spacing(2),
  paddingRight: 0, // Adjust padding right for close button alignment
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 auto',
  minWidth: 0, // allow the inner Typography to wrap within the paper
}));

const handleStopPropagation = (e?: React.SyntheticEvent, shouldStop?: boolean) => {
  if (shouldStop && e && typeof e.stopPropagation === 'function') {
    e.stopPropagation();
  }
};

const DialogBoxBase = (props: any) => {
  const { t } = useTranslation();
  const generatedTitleId = useId();
  const { title, dialogStyles, showCloseIcon = false, onClose, titleProps, centerTitle = false, disableBackdropClick = false, stopPropagation = true, useSaveCancelButtons = false,
        titleEndElement,
        saveCancelProps, isMarginTop = true, hideScrollbar = true, contentSx, footer, ...otherProps } = props;
  const { id: titleIdProp, ...titleSx } = titleProps ?? {};
  const titleId: string = titleIdProp ?? generatedTitleId;
  return (
    <>
      <DialogBoxStyle
        maxWidth={props.maxWidth}
        minWidth={props.minWidth}
        onClose={(event, reason) => {
          if (disableBackdropClick && reason === 'backdropClick') {
            return;
          }
          if (onClose) onClose();
          if (props?.handleClose) props.handleClose();
        }}
        aria-labelledby={titleId}
        open={props.open}
        dialogStyles={{ ...dialogStyles }}
        hideScrollbar={hideScrollbar}
        {...otherProps}
        onClick={e => handleStopPropagation(e, stopPropagation)}
        onMouseDown={e => handleStopPropagation(e, stopPropagation)}
        onMouseUp={e => handleStopPropagation(e, stopPropagation)}
        onKeyDown={e => handleStopPropagation(e, stopPropagation)}
        onWheel={e => handleStopPropagation(e, stopPropagation)}
        onTouchStart={e => handleStopPropagation(e, stopPropagation)}
      >
        <DialogTitleContainer
          style={centerTitle ? { justifyContent: 'center' } : {}}
        >
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', padding: '0', ...titleSx }}
            id={titleId}
          >
          {/* @ts-ignore */}
            <Typography
              fontSize={props.TitlefontSize}
              letterSpacing={0}
              variant="title"
              sx={{ minWidth: 0, overflowWrap: 'anywhere', ...(centerTitle ? { textAlign: 'center', width: '100%' } : {}) }}
            >
              {props.title}
            </Typography>
          </DialogTitle>
          {!!titleEndElement && <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>{titleEndElement}</Box>}
          {showCloseIcon && !centerTitle && (
            <IconButton aria-label={t('common.close', 'close')} onClick={onClose} sx={{ color: 'black', flexShrink: 0 }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitleContainer>

        <MuiDialogContent
          sx={{
            ...(contentSx || {}),
          }}
        >
          {props.children}
        </MuiDialogContent>
        {footer ? (
          <MuiDialogActions sx={{ display: 'block', flexShrink: 0, p: 0, pt: 4 }}>
            {footer}
          </MuiDialogActions>
        ) : useSaveCancelButtons ? (
          <MuiDialogActions
            sx={{
              display: 'flex',
              justifyContent: 'end',
              p: 0,
              pt: isMarginTop ? 2 : 0,
            }}
          >
            <SaveCancelButtons {...saveCancelProps} />
          </MuiDialogActions>
        ) : props.actions ? (
          <MuiDialogActions sx={{ display: 'flex', justifyContent: 'end', gap: 2, p: 0, pt: 2 }}>
            {props.actions.map(
              (action: {
                color?: any;
                textColor?: string;
                fullWidth?: boolean;
                variant?: any;
                text: string;
                disabled?: boolean;
                handler: MouseEventHandler<HTMLButtonElement> | undefined;
              }) => (
                <Button
                  sx={{ boxShadow: 'none' }}
                  disabled={action?.disabled}
                  color={action?.color}
                  variant={action?.variant}
                  fullWidth={action?.fullWidth}
                  onClick={action.handler}
                >
                  {action.text}
                </Button>
              )
            )}
          </MuiDialogActions>
        ) : null}
      </DialogBoxStyle>
    </>
  );
};

const DialogBox = withTracking<any>(DialogBoxBase, {
  lifecycle: {
    onMount: {
      event: MODAL_OPENED,
      mapPropsToProperties: (props) => ({
        title: typeof props.title === 'string' ? props.title : 'Unknown',
        url: window.location.pathname
      })
    }
  }
});

export default DialogBox;
